import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Visibility, Edit, Delete, Search } from "@mui/icons-material";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LiveList = () => {
  const [lives, setLives] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");

  const pageSize = 10;

  // ============================
  // FETCH BANNERS
  // ============================
  const fetchLives = async (page = 1, searchQuery = "") => {
    setLoading(true);
    try {
      const url = `${ENDPOINT.LIVE_LIST}?page=${page}&search=${encodeURIComponent(
        searchQuery
      )}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data;

      // *** IMPORTANT: YOUR RESPONSE USES "results" ***
      setLives(data.results || []);
      setCount(data.count || 0);
      setTotalPages(data.total_pages || 1);
      setCurrentPage(page);
    } catch (err) {
      toast.error("Failed to load lives");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if(!token){
      navigate("/login")
    }else{
      fetchLives(1, "");
    }
  }, []);

  // ============================
  // SEARCH
  // ============================
  const handleSearch = (e) => {
    e.preventDefault();
    fetchLives(1, search);
  };

  // ============================
  // PAGINATION
  // ============================
  const handleNext = () => {
    if (currentPage < totalPages) {
      fetchLives(currentPage + 1, search);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      fetchLives(currentPage - 1, search);
    }
  };

  // ============================
  // DELETE
  // ============================
  const openDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDialog(true);
  };

  const closeDeleteDialog = () => setOpenDialog(false);

  const handleDelete = async () => {
    try {
      await axios.delete(ENDPOINT.LIVE_DELETE(deleteId), {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Live deleted!");

      if (lives.length === 1 && currentPage > 1) {
        fetchLives(currentPage - 1, search);
      } else {
        fetchLives(currentPage, search);
      }
    } catch (err) {
      toast.error("Delete failed");
    } finally {
      closeDeleteDialog();
    }
  };

  // ============================
  // UI
  // ============================
  return (
    <Container maxWidth="xl" sx={{ mt: 5 }}>
      <ToastContainer />

      {/* SEARCH */}
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Box component="form" onSubmit={handleSearch}>
          <TextField
            size="small"
            placeholder="Search Live..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: 280 }}
          />

          <Button
            type="submit"
            variant="contained"
            sx={{ ml: 1 }}
            disabled={loading}
          >
            Search
          </Button>
        </Box>

        <Button
          variant="contained"
          color="success"
          onClick={() => navigate("/live/create")}
        >
          + Create Live
        </Button>
      </Stack>

      {/* TABLE */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>No</TableCell>
                  <TableCell>Live Catgeory</TableCell>
                  <TableCell>League</TableCell>
                  <TableCell>Team1</TableCell>
                  <TableCell>Team2</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {lives.length ? (
                  lives.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {(currentPage - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>{item.category.name}</TableCell>

                      <TableCell>{item.league.name}</TableCell>

                      <TableCell>
                        <Avatar
                          src={`${IMAGE_BASE_URL}${item.team1.image}`}
                          variant="rounded"
                          sx={{ width: 50, height: 50 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Avatar
                          src={`${IMAGE_BASE_URL}${item.team2.image}`}
                          variant="rounded"
                          sx={{ width: 50, height: 50 }}
                        />
                      </TableCell>

                      <TableCell>{item.startdate}</TableCell>

                      <TableCell>{item.starttime}</TableCell>

                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <Tooltip title="View">
                            <IconButton
                              onClick={() => navigate(`/live/${item.id}`)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Update">
                            <IconButton
                              color="warning"
                              onClick={() => navigate(`/live/update/${item.id}`)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => openDeleteDialog(item.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No lives found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* PAGINATION */}
          <Divider />

          <Stack direction="row" justifyContent="space-between" p={2}>
            <Typography>
              Total: <b>{count}</b> | Pages: <b>{totalPages}</b>
            </Typography>

            <Stack direction="row" spacing={2}>
              <Button disabled={currentPage <= 1} onClick={handlePrevious}>
                ← Previous
              </Button>

              <Typography>
                Page {currentPage} / {totalPages}
              </Typography>

              <Button disabled={currentPage >= totalPages} onClick={handleNext}>
                Next →
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* DELETE DIALOG */}
      <Dialog open={openDialog} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this live?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LiveList;
